<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { onClickOutside, useResizeObserver } from '@vueuse/core'
import { useToast } from 'vue-toastification'
import { localeTitle, supportedLanguages } from '@/plugins/i18n'
import { useLanguageService } from '@/composables/language'
import { isUserLoggedIn } from '@/auth/utils'
import { useAuthStore } from '@/stores/auth'
import { useWorkspaceStore } from '@/stores/workspace'
import { useNotificationsStore } from '@/stores/notifications'
import { usePriceStore } from '@/stores/price'
import LandingAccountMenu from '@/components/layout/LandingAccountMenu.vue'
import NotificationBell from '@/components/layout/NotificationBell.vue'
import { landingUrl } from '@/utils/landing'
import { accountMenuLabel } from '@/utils/workspace-label'
import logoLight from '@images/swida/logo-light.svg'
import flagEn from '@images/swida/flag-en.svg'
import flagDe from '@images/swida/flag-de.svg'

const props = defineProps({
  // The page top (the home hero) shows through the translucent header
  // instead of the black band reserved under it on the other pages.
  overlay: {
    type: Boolean,
    default: false,
  },
})

interface UserSubmenuItem {
  label: string
  to: string

  // Sub-pages the entry stays highlighted on.
  activeOn?: string[]
}

const FLAGS: Record<string, string> = { en: flagEn, de: flagDe }

// Width of the fade over an edge of the workspace row (see .fade-start/.fade-end).
const USER_SUBMENU_FADE = 48

// The fixed header's current height, published on <html> for the toasts
// (assets/styles/toast.css) and the anchors the router scrolls to (router/scroll.ts).
const HEADER_HEIGHT_PROPERTY = '--sw-header-height'

// Where the header is rendered: a container at the top of <body>, outside the
// page, so that keyboard and screen reader users meet the header first.
const HEADER_ROOT_ID = 'sw-header-root'

function headerRoot(): HTMLElement {
  let root = document.getElementById(HEADER_ROOT_ID)

  if (!root) {
    root = document.createElement('div')
    root.id = HEADER_ROOT_ID
    document.body.prepend(root)
  }

  return root
}

interface NavItem {
  label: string
  href: string
  hash?: string
}

const navItems: NavItem[] = [
  { label: 'How it works', href: landingUrl('/insta-carrier') },
  { label: 'Contact us', href: '/#contact', hash: 'contact' },
  { label: 'Blog', href: landingUrl('/blog') },
]

const { t } = useI18n()
const toast = useToast()
const route = useRoute()
const languageService = useLanguageService()
const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()
const notificationsStore = useNotificationsStore()
const priceStore = usePriceStore()

const headerTarget = headerRoot()
const headerRef = ref<HTMLElement | null>(null)
const spacerRef = ref<HTMLElement | null>(null)
const userSubmenuRef = ref<HTMLElement | null>(null)
const userSubmenuListRef = ref<HTMLElement | null>(null)
const isScrolled = ref(false)
const isMenuOpen = ref(false)
const langMenuOpen = ref(false)
const mobileLangMenuOpen = ref(false)
const userMenuOpen = ref(false)
const mobileUserMenuOpen = ref(false)
const notifMenuOpen = ref(false)
const activeWorkspacePending = ref(false)
const userSubmenuEdges = ref({ start: false, end: false })

const isLoggedIn = computed(() => isUserLoggedIn())
const userEmail = computed(() => authStore.currentUser?.email || '')
const currentLanguage = computed(() => languageService.currentLanguage.value)
const otherLanguages = computed(() => supportedLanguages.filter(lang => lang !== currentLanguage.value))

// Who the user is acting as: the same label the workspace switcher shows
// under "Current". Left empty while the active workspace loads: until then it
// would be the e-mail, also for a company.
const accountLabel = computed(() => activeWorkspacePending.value
  ? ''
  : accountMenuLabel(workspaceStore.activeWorkspace, userEmail.value, t('User Menu')))

const userSubmenuItems = computed<UserSubmenuItem[]>(() => {
  const items: UserSubmenuItem[] = []
  const ws = workspaceStore.activeWorkspace

  // Admins always have every permission (backend fills all flags as true
  // for admin memberships); members see only what's been granted.
  const perms = ws?.current_user_permissions ?? {
    can_access_invoices: false,
    can_access_company_details: false,
    can_access_loyalty: false,
  }

  // Tabs other than Orders require a verified workspace linked to Swida
  // (company_id). Orders is universally accessible — it falls back to local
  // IC data when the workspace isn't linked to Swida.
  const hasSwidaLink = Boolean(ws?.is_verified && ws?.company_id)

  // Orders is shown for the personal workspace and for verified
  // Swida-linked companies. Unverified company drafts get no Orders
  // tab — there is nothing to show until verification links them to Swida.
  const isPersonalWorkspace = ws?.workspace_type === 'personal'
  if (ws && (isPersonalWorkspace || hasSwidaLink))
    items.push({ label: t('Orders'), to: '/workspace/orders' })

  if (perms.can_access_invoices && hasSwidaLink)
    items.push({ label: t('Invoices'), to: '/workspace/invoices' })

  if (perms.can_access_company_details && hasSwidaLink)
    items.push({ label: t('Company Details'), to: '/workspace/company-details' })

  // Loyalty sits behind the admin feature switch; while it is off the entry
  // is dropped for everyone, permission flag and Swida link notwithstanding.
  if (priceStore.isLoyaltyEnabled && perms.can_access_loyalty && hasSwidaLink)
    items.push({ label: t('Loyalty program'), to: '/workspace/loyalty-program', activeOn: ['/workspace/loyalty-orders'] })

  return items
})

// The row stays reserved while the active workspace loads, so the page does
// not jump down when the links arrive.
const showUserSubmenu = computed(() =>
  isLoggedIn.value && (userSubmenuItems.value.length > 0 || activeWorkspacePending.value),
)

function isUserSubmenuItemActive(item: UserSubmenuItem) {
  return [item.to, ...(item.activeOn ?? [])].some(path => route.path.startsWith(path))
}

// On phones the row scrolls sideways when the links do not fit; keep the
// current page in view, clear of the faded edges.
function revealActiveUserSubmenuItem() {
  const scroller = userSubmenuRef.value
  const active = scroller?.querySelector<HTMLElement>('.is-active')
  if (!scroller || !active)
    return

  const { offsetLeft, offsetWidth } = active
  const { scrollLeft, clientWidth } = scroller

  if (offsetLeft - USER_SUBMENU_FADE < scrollLeft || offsetLeft + offsetWidth + USER_SUBMENU_FADE > scrollLeft + clientWidth)
    scroller.scrollLeft = offsetLeft - (clientWidth - offsetWidth) / 2
}

// Fades an edge of the row only while more links hide past it.
function updateUserSubmenuEdges() {
  const scroller = userSubmenuRef.value
  if (!scroller)
    return

  const { scrollLeft, scrollWidth, clientWidth } = scroller

  userSubmenuEdges.value = {
    start: scrollLeft > 1,
    end: scrollLeft + clientWidth < scrollWidth - 1,
  }
}

function closeDropdowns() {
  langMenuOpen.value = false
  mobileLangMenuOpen.value = false
  userMenuOpen.value = false
  mobileUserMenuOpen.value = false
  notifMenuOpen.value = false
}

function closeMenu() {
  closeDropdowns()
  isMenuOpen.value = false
}

function onNavClick(item: NavItem, event: Event) {
  closeMenu()

  const el = item.hash ? document.getElementById(item.hash) : null
  if (el) {
    event.preventDefault()

    // Lands the section below the fixed header instead of under it.
    el.style.scrollMarginTop = `${spacerRef.value?.offsetHeight ?? 0}px`
    el.scrollIntoView({ behavior: 'smooth' })
  }
}

function toggleMenu() {
  const open = !isMenuOpen.value

  closeMenu()
  isMenuOpen.value = open
}

function toggleLangMenu() {
  const open = !langMenuOpen.value

  closeDropdowns()
  langMenuOpen.value = open
}

function toggleMobileLangMenu() {
  const open = !mobileLangMenuOpen.value

  closeDropdowns()
  mobileLangMenuOpen.value = open
}

function toggleUserMenu() {
  const open = !userMenuOpen.value

  closeDropdowns()
  userMenuOpen.value = open
}

function toggleMobileUserMenu() {
  const open = !mobileUserMenuOpen.value

  closeDropdowns()
  mobileUserMenuOpen.value = open
}

function toggleNotifMenu() {
  const open = !notifMenuOpen.value

  closeDropdowns()
  notifMenuOpen.value = open
}

async function setLanguage(lang: string) {
  closeDropdowns()
  try {
    await languageService.setLanguage(lang)
  }
  catch (err) {
    console.error(err)
    toast.error('Could not set language. Try again later.')
  }
}

// The IC body is the scroll container (html/body overflow-x: hidden), not the window.
function updateScrolled() {
  isScrolled.value = Math.max(window.scrollY, document.documentElement.scrollTop, document.body.scrollTop) > 24
}

function lockPageScroll(locked: boolean) {
  document.documentElement.style.overflow = locked ? 'hidden' : ''
  document.body.style.overflow = locked ? 'hidden' : ''
}

function onResize() {
  if (window.matchMedia('(min-width: 1280px)').matches)
    isMenuOpen.value = false
}

// Toasts drop in below the header instead of covering it or hiding under it,
// and anchors land below it. The header grows with the workspace row and the
// open mobile menu.
function publishHeaderHeight() {
  const height = Math.ceil(headerRef.value?.getBoundingClientRect().height ?? 0)

  document.documentElement.style.setProperty(HEADER_HEIGHT_PROPERTY, `${height}px`)
}

onMounted(() => {
  // Already before the first resize callback: on a page load the router
  // scrolls to an anchor right after this.
  publishHeaderHeight()
  updateScrolled()
  window.addEventListener('scroll', updateScrolled, { passive: true, capture: true })
  window.addEventListener('resize', onResize)
})

onUnmounted(() => {
  window.removeEventListener('scroll', updateScrolled, { capture: true })
  window.removeEventListener('resize', onResize)
  lockPageScroll(false)

  // Pages without this header leave the toasts where the library puts them.
  document.documentElement.style.removeProperty(HEADER_HEIGHT_PROPERTY)
})

useResizeObserver(headerRef, publishHeaderHeight)

watch(isMenuOpen, lockPageScroll)

watch(() => route.fullPath, () => {
  closeMenu()
  nextTick(updateScrolled)
})

// The links resize when they first render, when the web font arrives and
// when the language changes.
useResizeObserver(userSubmenuListRef, () => {
  revealActiveUserSubmenuItem()
  updateUserSubmenuEdges()
})
watch(() => route.path, revealActiveUserSubmenuItem, { flush: 'post' })

watch(isLoggedIn, async (loggedIn, wasLoggedIn) => {
  if (!loggedIn) {
    userMenuOpen.value = false
    mobileUserMenuOpen.value = false
    notifMenuOpen.value = false
    notificationsStore.stopPolling()

    // The next sign-in may be another user's, without a reload: the header must
    // not show the previous session's workspace until the new one has loaded.
    workspaceStore.clearSession()

    // Nor the previous user: a sign-out in another tab (the tokens are shared)
    // leaves this tab's copy behind, and the next sign-in would keep it.
    authStore.clear()

    return
  }

  // Again on sign-in (not on a remount of the header), so that answers to
  // requests sent before it are dropped.
  if (wasLoggedIn === false)
    workspaceStore.clearSession()

  const session = workspaceStore.session

  activeWorkspacePending.value = !workspaceStore.activeWorkspace

  if (!authStore.currentUser) {
    try {
      await authStore.fetchCurrentUser()
    }
    catch (err) {
      console.error(err)
    }

    // Signed out meanwhile.
    if (session !== workspaceStore.session)
      return
  }

  // The workspace row and the account button need the active workspace;
  // the switcher in the account menu also needs the list of switchable
  // workspaces and the user's outgoing pending join requests.
  workspaceStore.fetchActiveWorkspace().finally(() => {
    // A later session waits for its own answer.
    if (session === workspaceStore.session)
      activeWorkspacePending.value = false
  })
  workspaceStore.fetchWorkspaces()
  workspaceStore.fetchMyJoinRequests()

  // The Loyalty entry hangs off the settings singleton. main.ts warms it on
  // boot; this retries it if that request failed.
  priceStore.ensureSettingsLoaded()

  // Notification centre: keep the bell badge fresh while logged in.
  notificationsStore.startPolling()
}, { immediate: true })

onClickOutside(headerRef, closeDropdowns)
</script>

<template>
  <div class="landing-navbar">
    <!-- Rendered outside the page so a page's stacking context (e.g. the home hero) cannot cover it, and ahead of it in the tab order. -->
    <Teleport :to="headerTarget">
      <header ref="headerRef" class="sw-header" :class="{ 'sw-header--solid': isScrolled || isMenuOpen }">
        <div class="sw-container-wide sw-header-bar">
          <div class="sw-header-row">
            <a :href="landingUrl('/')" aria-label="Swida" class="sw-logo-link">
              <img
                :src="logoLight" alt="Swida Logo"
                width="95"
                height="23"
              >
            </a>

            <ul class="main-menu">
              <li v-for="item in navItems" :key="item.label" class="main-menu-item">
                <a :href="item.href" class="main-menu-item-link" @click="onNavClick(item, $event)">
                  {{ $t(item.label) }}
                </a>
              </li>
            </ul>

            <div class="sw-header-actions">
              <div
                class="lang-switcher"
                @mouseenter="langMenuOpen = true"
                @mouseleave="langMenuOpen = false"
              >
                <button
                  type="button"
                  class="lang-switcher-button"
                  aria-haspopup="true"
                  :aria-expanded="langMenuOpen"
                  :aria-label="`${$t('Change language')} (${localeTitle(currentLanguage)})`"
                  @click.stop="toggleLangMenu"
                >
                  <svg
                    width="16" height="16"
                    viewBox="0 0 16 16"
                    fill="none" xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path d="M4.80005 1.6C5.24255 1.6 5.60005 1.9575 5.60005 2.4V3.2H8.80005C9.24255 3.2 9.60005 3.5575 9.60005 4C9.60005 4.4425 9.24255 4.8 8.80005 4.8H8.56005L8.35005 5.3775C7.94005 6.5075 7.32255 7.54 6.54505 8.4275C6.90005 8.6475 7.27005 8.8425 7.65505 9.015L8.91505 9.575L10.47 6.075C10.5975 5.785 10.885 5.6 11.2 5.6C11.515 5.6 11.8025 5.785 11.93 6.075L15.13 13.275C15.31 13.68 15.1275 14.1525 14.725 14.33C14.3225 14.5075 13.8475 14.3275 13.67 13.925L13.17 12.8H9.23255L8.73255 13.925C8.55255 14.33 8.08005 14.51 7.67755 14.33C7.27505 14.15 7.09255 13.6775 7.27255 13.275L8.26755 11.0375L7.00755 10.4775C6.43255 10.2225 5.88255 9.9175 5.36255 9.5675C4.83005 9.9975 4.24755 10.3725 3.62505 10.685L2.75755 11.115C2.36255 11.3125 1.88255 11.1525 1.68505 10.7575C1.48755 10.3625 1.64755 9.8825 2.04255 9.685L2.90505 9.2525C3.31255 9.0475 3.70005 8.81 4.06505 8.545C3.72005 8.2275 3.39505 7.885 3.09255 7.5225L2.84005 7.2175C2.55755 6.8775 2.60255 6.3725 2.94255 6.09C3.28255 5.8075 3.78755 5.8525 4.07005 6.1925L4.32505 6.4975C4.61255 6.845 4.92755 7.1675 5.26005 7.465C5.94755 6.705 6.49005 5.8125 6.84755 4.83L6.86005 4.8H1.60255C1.15755 4.8 0.800049 4.4425 0.800049 4C0.800049 3.5575 1.15755 3.2 1.60005 3.2H4.00005V2.4C4.00005 1.9575 4.35755 1.6 4.80005 1.6ZM11.2 8.37L9.94255 11.2H12.4575L11.2 8.37Z" fill="currentColor" />
                  </svg>
                  <span class="lang-switcher-code">{{ currentLanguage }}</span>
                  <svg
                    width="14" height="14"
                    viewBox="0 0 14 14"
                    fill="none" xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path d="M6.56903 9.6425C6.84465 9.86562 7.24934 9.85031 7.50528 9.59437L10.3053 6.79437C10.5065 6.59312 10.5656 6.29344 10.4562 6.03094C10.3468 5.76844 10.0931 5.6 9.8109 5.6H4.2109C3.92872 5.6 3.67278 5.77062 3.5634 6.03312C3.45403 6.29562 3.51528 6.59531 3.71653 6.79437L6.51653 9.59437L6.56903 9.6425Z" fill="currentColor" />
                  </svg>
                </button>
                <div v-if="langMenuOpen" class="lang-switcher-menu-wrapper">
                  <ul class="lang-switcher-menu">
                    <li v-for="lang in otherLanguages" :key="lang" class="lang-switcher-menu-item">
                      <button type="button" class="lang-switcher-menu-item-link" @click="setLanguage(lang)">
                        <img :src="FLAGS[lang]" alt="" class="lang-switcher-flag">
                        <span>{{ localeTitle(lang) }}</span>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

              <RouterLink v-if="!isLoggedIn" :to="{ name: 'login' }" class="sw-button-primary sw-sign-in">
                <svg
                  width="20" height="20"
                  viewBox="0 0 20 20"
                  fill="none" xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path d="M10 9.75C10.4925 9.75 10.9801 9.653 11.4351 9.46455C11.89 9.27609 12.3034 8.99987 12.6517 8.65165C12.9999 8.30343 13.2761 7.89003 13.4645 7.43506C13.653 6.98009 13.75 6.49246 13.75 6C13.75 5.50754 13.653 5.01991 13.4645 4.56494C13.2761 4.10997 12.9999 3.69657 12.6517 3.34835C12.3034 3.00013 11.89 2.72391 11.4351 2.53545C10.9801 2.347 10.4925 2.25 10 2.25C9.50754 2.25 9.01991 2.347 8.56494 2.53545C8.10997 2.72391 7.69657 3.00013 7.34835 3.34835C7.00013 3.69657 6.72391 4.10997 6.53545 4.56494C6.347 5.01991 6.25 5.50754 6.25 6C6.25 6.49246 6.347 6.98009 6.53545 7.43506C6.72391 7.89003 7.00013 8.30343 7.34835 8.65165C7.69657 8.99987 8.10997 9.27609 8.56494 9.46455C9.01991 9.653 9.50754 9.75 10 9.75ZM9.07188 11.5C5.99375 11.5 3.5 13.9937 3.5 17.0719C3.5 17.5844 3.91563 18 4.42813 18H15.5719C16.0844 18 16.5 17.5844 16.5 17.0719C16.5 13.9937 14.0063 11.5 10.9281 11.5H9.07188Z" fill="currentColor" />
                </svg>
                <span class="sw-sign-in-label">{{ $t('Sign In') }}</span>
                <svg
                  width="20" height="20"
                  viewBox="0 0 20 20"
                  fill="none" xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path d="M13.7219 9.29375C14.1125 9.68438 14.1125 10.3188 13.7219 10.7094L8.7219 15.7094C8.33127 16.1 7.6969 16.1 7.30627 15.7094C6.91565 15.3188 6.91565 14.6844 7.30627 14.2938L11.6 10L7.3094 5.70625C6.91877 5.31563 6.91877 4.68125 7.3094 4.29063C7.70002 3.9 8.3344 3.9 8.72502 4.29063L13.725 9.29063L13.7219 9.29375Z" fill="currentColor" />
                </svg>
              </RouterLink>
              <template v-else>
                <NotificationBell
                  :open="notifMenuOpen"
                  @toggle="toggleNotifMenu"
                  @close="notifMenuOpen = false"
                />
                <div class="sw-account">
                  <button
                    type="button"
                    class="sw-button-primary sw-sign-in"
                    :title="accountLabel || undefined"
                    :aria-label="$t('User Menu')"
                    :aria-expanded="userMenuOpen"
                    @click="toggleUserMenu"
                  >
                    <svg
                      width="20" height="20"
                      viewBox="0 0 20 20"
                      fill="none" xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path d="M10 9.75C10.4925 9.75 10.9801 9.653 11.4351 9.46455C11.89 9.27609 12.3034 8.99987 12.6517 8.65165C12.9999 8.30343 13.2761 7.89003 13.4645 7.43506C13.653 6.98009 13.75 6.49246 13.75 6C13.75 5.50754 13.653 5.01991 13.4645 4.56494C13.2761 4.10997 12.9999 3.69657 12.6517 3.34835C12.3034 3.00013 11.89 2.72391 11.4351 2.53545C10.9801 2.347 10.4925 2.25 10 2.25C9.50754 2.25 9.01991 2.347 8.56494 2.53545C8.10997 2.72391 7.69657 3.00013 7.34835 3.34835C7.00013 3.69657 6.72391 4.10997 6.53545 4.56494C6.347 5.01991 6.25 5.50754 6.25 6C6.25 6.49246 6.347 6.98009 6.53545 7.43506C6.72391 7.89003 7.00013 8.30343 7.34835 8.65165C7.69657 8.99987 8.10997 9.27609 8.56494 9.46455C9.01991 9.653 9.50754 9.75 10 9.75ZM9.07188 11.5C5.99375 11.5 3.5 13.9937 3.5 17.0719C3.5 17.5844 3.91563 18 4.42813 18H15.5719C16.0844 18 16.5 17.5844 16.5 17.0719C16.5 13.9937 14.0063 11.5 10.9281 11.5H9.07188Z" fill="currentColor" />
                    </svg>
                    <span class="sw-sign-in-label sw-account-label" :class="{ 'is-loading': !accountLabel }">{{ accountLabel }}</span>
                    <svg
                      class="sw-account-chevron" :class="{ 'is-open': userMenuOpen }"
                      width="14"
                      height="14" viewBox="0 0 14 14"
                      fill="none" xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path d="M6.56903 9.6425C6.84465 9.86562 7.24934 9.85031 7.50528 9.59437L10.3053 6.79437C10.5065 6.59312 10.5656 6.29344 10.4562 6.03094C10.3468 5.76844 10.0931 5.6 9.8109 5.6H4.2109C3.92872 5.6 3.67278 5.77062 3.5634 6.03312C3.45403 6.29562 3.51528 6.59531 3.71653 6.79437L6.51653 9.59437L6.56903 9.6425Z" fill="currentColor" />
                    </svg>
                  </button>
                  <div v-if="userMenuOpen" class="sw-account-dropdown">
                    <div class="sw-dropdown-panel">
                      <LandingAccountMenu @close="closeDropdowns" />
                    </div>
                  </div>
                </div>
              </template>
            </div>

            <button
              type="button"
              class="hamburger"
              :aria-expanded="isMenuOpen"
              :aria-label="isMenuOpen ? $t('Close menu') : $t('Open menu')"
              @click="toggleMenu"
            >
              <span class="hamburger-box" aria-hidden="true">
                <span class="hamburger-inner" :class="{ 'is-open': isMenuOpen }" />
              </span>
            </button>
          </div>
        </div>

        <!-- The open mobile menu lists the same links, so the row folds away (and out of the tab order) meanwhile. -->
        <div
          v-if="showUserSubmenu"
          class="sw-subnav"
          :class="{ 'is-collapsed': isMenuOpen }"
          :inert="isMenuOpen"
        >
          <div class="sw-subnav-clip">
            <nav
              ref="userSubmenuRef"
              class="sw-subnav-scroll"
              :class="{ 'fade-start': userSubmenuEdges.start, 'fade-end': userSubmenuEdges.end }"
              :aria-label="accountLabel || undefined"
              @scroll.passive="updateUserSubmenuEdges"
            >
              <ul ref="userSubmenuListRef" class="sw-subnav-list">
                <li v-for="item in userSubmenuItems" :key="item.to">
                  <RouterLink
                    :to="item.to"
                    class="sw-subnav-link"
                    :class="{ 'is-active': isUserSubmenuItemActive(item) }"
                    :aria-current="isUserSubmenuItemActive(item) ? 'page' : undefined"
                    @click="closeMenu"
                  >
                    {{ item.label }}
                  </RouterLink>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <!-- Folded away it is out of the tab order too. -->
        <div class="mobile-menu" :class="{ 'is-open': isMenuOpen }" :inert="!isMenuOpen">
          <div class="mobile-menu-clip">
            <nav class="mobile-menu-panel" aria-label="Main">
              <div class="mobile-menu-scroll">
                <ul v-if="isLoggedIn && userSubmenuItems.length > 0" class="mobile-menu-list mobile-menu-list--account">
                  <li v-for="item in userSubmenuItems" :key="item.to">
                    <RouterLink
                      :to="item.to"
                      class="mobile-menu-link"
                      :class="{ 'is-active': isUserSubmenuItemActive(item) }"
                      :aria-current="isUserSubmenuItemActive(item) ? 'page' : undefined"
                      @click="closeMenu"
                    >
                      {{ item.label }}
                    </RouterLink>
                  </li>
                </ul>
                <ul class="mobile-menu-list">
                  <li v-for="item in navItems" :key="item.label">
                    <a :href="item.href" class="mobile-menu-link" @click="onNavClick(item, $event)">
                      {{ $t(item.label) }}
                    </a>
                  </li>
                </ul>
              </div>

              <div class="mobile-menu-footer">
                <div class="lang-switcher lang-switcher--up">
                  <button
                    type="button"
                    class="lang-switcher-button"
                    aria-haspopup="true"
                    :aria-expanded="mobileLangMenuOpen"
                    :aria-label="`${$t('Change language')} (${localeTitle(currentLanguage)})`"
                    @click.stop="toggleMobileLangMenu"
                  >
                    <svg
                      width="16" height="16"
                      viewBox="0 0 16 16"
                      fill="none" xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path d="M4.80005 1.6C5.24255 1.6 5.60005 1.9575 5.60005 2.4V3.2H8.80005C9.24255 3.2 9.60005 3.5575 9.60005 4C9.60005 4.4425 9.24255 4.8 8.80005 4.8H8.56005L8.35005 5.3775C7.94005 6.5075 7.32255 7.54 6.54505 8.4275C6.90005 8.6475 7.27005 8.8425 7.65505 9.015L8.91505 9.575L10.47 6.075C10.5975 5.785 10.885 5.6 11.2 5.6C11.515 5.6 11.8025 5.785 11.93 6.075L15.13 13.275C15.31 13.68 15.1275 14.1525 14.725 14.33C14.3225 14.5075 13.8475 14.3275 13.67 13.925L13.17 12.8H9.23255L8.73255 13.925C8.55255 14.33 8.08005 14.51 7.67755 14.33C7.27505 14.15 7.09255 13.6775 7.27255 13.275L8.26755 11.0375L7.00755 10.4775C6.43255 10.2225 5.88255 9.9175 5.36255 9.5675C4.83005 9.9975 4.24755 10.3725 3.62505 10.685L2.75755 11.115C2.36255 11.3125 1.88255 11.1525 1.68505 10.7575C1.48755 10.3625 1.64755 9.8825 2.04255 9.685L2.90505 9.2525C3.31255 9.0475 3.70005 8.81 4.06505 8.545C3.72005 8.2275 3.39505 7.885 3.09255 7.5225L2.84005 7.2175C2.55755 6.8775 2.60255 6.3725 2.94255 6.09C3.28255 5.8075 3.78755 5.8525 4.07005 6.1925L4.32505 6.4975C4.61255 6.845 4.92755 7.1675 5.26005 7.465C5.94755 6.705 6.49005 5.8125 6.84755 4.83L6.86005 4.8H1.60255C1.15755 4.8 0.800049 4.4425 0.800049 4C0.800049 3.5575 1.15755 3.2 1.60005 3.2H4.00005V2.4C4.00005 1.9575 4.35755 1.6 4.80005 1.6ZM11.2 8.37L9.94255 11.2H12.4575L11.2 8.37Z" fill="currentColor" />
                    </svg>
                    <span class="lang-switcher-code">{{ currentLanguage }}</span>
                    <svg
                      width="14" height="14"
                      viewBox="0 0 14 14"
                      fill="none" xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path d="M6.56903 9.6425C6.84465 9.86562 7.24934 9.85031 7.50528 9.59437L10.3053 6.79437C10.5065 6.59312 10.5656 6.29344 10.4562 6.03094C10.3468 5.76844 10.0931 5.6 9.8109 5.6H4.2109C3.92872 5.6 3.67278 5.77062 3.5634 6.03312C3.45403 6.29562 3.51528 6.59531 3.71653 6.79437L6.51653 9.59437L6.56903 9.6425Z" fill="currentColor" />
                    </svg>
                  </button>
                  <div v-if="mobileLangMenuOpen" class="lang-switcher-menu-wrapper">
                    <ul class="lang-switcher-menu">
                      <li v-for="lang in otherLanguages" :key="lang" class="lang-switcher-menu-item">
                        <button type="button" class="lang-switcher-menu-item-link" @click="setLanguage(lang)">
                          <img :src="FLAGS[lang]" alt="" class="lang-switcher-flag">
                          <span>{{ localeTitle(lang) }}</span>
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>

                <RouterLink
                  v-if="!isLoggedIn" :to="{ name: 'login' }"
                  class="sw-button-primary sw-sign-in"
                  @click="closeMenu"
                >
                  <svg
                    width="20" height="20"
                    viewBox="0 0 20 20"
                    fill="none" xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path d="M10 9.75C10.4925 9.75 10.9801 9.653 11.4351 9.46455C11.89 9.27609 12.3034 8.99987 12.6517 8.65165C12.9999 8.30343 13.2761 7.89003 13.4645 7.43506C13.653 6.98009 13.75 6.49246 13.75 6C13.75 5.50754 13.653 5.01991 13.4645 4.56494C13.2761 4.10997 12.9999 3.69657 12.6517 3.34835C12.3034 3.00013 11.89 2.72391 11.4351 2.53545C10.9801 2.347 10.4925 2.25 10 2.25C9.50754 2.25 9.01991 2.347 8.56494 2.53545C8.10997 2.72391 7.69657 3.00013 7.34835 3.34835C7.00013 3.69657 6.72391 4.10997 6.53545 4.56494C6.347 5.01991 6.25 5.50754 6.25 6C6.25 6.49246 6.347 6.98009 6.53545 7.43506C6.72391 7.89003 7.00013 8.30343 7.34835 8.65165C7.69657 8.99987 8.10997 9.27609 8.56494 9.46455C9.01991 9.653 9.50754 9.75 10 9.75ZM9.07188 11.5C5.99375 11.5 3.5 13.9937 3.5 17.0719C3.5 17.5844 3.91563 18 4.42813 18H15.5719C16.0844 18 16.5 17.5844 16.5 17.0719C16.5 13.9937 14.0063 11.5 10.9281 11.5H9.07188Z" fill="currentColor" />
                  </svg>
                  <span class="sw-sign-in-label">{{ $t('Sign In') }}</span>
                  <svg
                    width="20" height="20"
                    viewBox="0 0 20 20"
                    fill="none" xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path d="M13.7219 9.29375C14.1125 9.68438 14.1125 10.3188 13.7219 10.7094L8.7219 15.7094C8.33127 16.1 7.6969 16.1 7.30627 15.7094C6.91565 15.3188 6.91565 14.6844 7.30627 14.2938L11.6 10L7.3094 5.70625C6.91877 5.31563 6.91877 4.68125 7.3094 4.29063C7.70002 3.9 8.3344 3.9 8.72502 4.29063L13.725 9.29063L13.7219 9.29375Z" fill="currentColor" />
                  </svg>
                </RouterLink>
                <template v-else>
                  <div class="mobile-menu-account">
                    <NotificationBell
                      :open="notifMenuOpen"
                      @toggle="toggleNotifMenu"
                      @close="notifMenuOpen = false"
                    />
                    <button
                      type="button"
                      class="sw-button-primary sw-sign-in"
                      :title="accountLabel || undefined"
                      :aria-label="$t('User Menu')"
                      :aria-expanded="mobileUserMenuOpen"
                      @click="toggleMobileUserMenu"
                    >
                      <span class="sw-sign-in-label sw-account-label" :class="{ 'is-loading': !accountLabel }">{{ accountLabel }}</span>
                      <svg
                        class="sw-account-chevron" :class="{ 'is-open': mobileUserMenuOpen }"
                        width="14"
                        height="14" viewBox="0 0 14 14"
                        fill="none" xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path d="M6.56903 9.6425C6.84465 9.86562 7.24934 9.85031 7.50528 9.59437L10.3053 6.79437C10.5065 6.59312 10.5656 6.29344 10.4562 6.03094C10.3468 5.76844 10.0931 5.6 9.8109 5.6H4.2109C3.92872 5.6 3.67278 5.77062 3.5634 6.03312C3.45403 6.29562 3.51528 6.59531 3.71653 6.79437L6.51653 9.59437L6.56903 9.6425Z" fill="currentColor" />
                      </svg>
                    </button>
                  </div>
                  <div v-if="mobileUserMenuOpen" class="mobile-menu-account-panel">
                    <LandingAccountMenu @close="closeMenu" />
                  </div>
                </template>
              </div>
            </nav>
          </div>
        </div>
      </header>

      <Transition name="mobile-menu-overlay">
        <div
          v-if="isMenuOpen"
          class="landing-navbar-overlay"
          aria-hidden="true"
          @click="closeMenu"
        />
      </Transition>
    </Teleport>

    <!-- Reserves the fixed header's height, the workspace row included. -->
    <div
      ref="spacerRef"
      class="sw-header-spacer"
      :class="{ 'sw-header-spacer--overlay': props.overlay, 'sw-header-spacer--subnav': showUserSubmenu }"
    />
  </div>
</template>

<style scoped>
.sw-header {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999999;
  width: 100%;
  background-color: rgb(0 0 0 / 0.4);
  -webkit-backdrop-filter: blur(12px);
  backdrop-filter: blur(12px);
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  transition: background-color 300ms cubic-bezier(0.4, 0, 0.2, 1), backdrop-filter 300ms cubic-bezier(0.4, 0, 0.2, 1);
}

.sw-header--solid {
  background-color: #0E0F10;
}

.sw-header *,
.sw-header *::before,
.sw-header *::after {
  box-sizing: border-box;
}

.sw-header ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.sw-header a {
  color: inherit;
  text-decoration: none;
}

.sw-header button {
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.sw-header img,
.sw-header svg {
  display: block;
  vertical-align: middle;
}

.sw-container-wide {
  position: relative;
  width: 100%;
  max-width: min(1440px, 90%);
  margin: 0 auto;
}

.sw-header-bar {
  min-height: 80px;
  padding-top: 16px;
  padding-bottom: 16px;
}

.sw-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  width: 100%;
  height: 100%;
}

.sw-logo-link {
  flex-shrink: 0;
}

.main-menu {
  display: none !important;
  align-items: baseline;
  gap: 40px;
}

.main-menu-item {
  position: relative;
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.main-menu-item-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  line-height: 1.25;
}

.main-menu-item-link:hover {
  color: #FFFFFF;
}

.sw-account-chevron {
  flex-shrink: 0;
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.sw-account-chevron.is-open {
  transform: rotate(180deg);
}

.sw-header-actions {
  display: none;
  align-items: center;
  gap: 34px;
}

.lang-switcher {
  position: relative;
  display: inline-block;
}

.lang-switcher-button {
  display: flex !important;
  align-items: center;
  gap: 6px;
  color: #FFFFFF !important;
  user-select: none;
}

.lang-switcher-code {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-transform: uppercase;
}

.lang-switcher-menu-wrapper {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 40;
  padding-top: 8px;
}

.lang-switcher-menu {
  width: max-content;
  max-width: 270px;
  padding: 8px 12px !important;
  border: 1px solid #C1C1CC;
  border-radius: 12px;
  background-color: #FFFFFF;
  box-shadow: 9px 8px 32px 0 rgb(0 0 0 / 0.1);
}

.lang-switcher-menu-item {
  border-bottom: 1px solid #E2E3E9;
}

.lang-switcher-menu-item + .lang-switcher-menu-item {
  margin-top: 10px;
}

.lang-switcher-menu-item:last-of-type {
  border: none;
}

.lang-switcher-menu-item-link {
  display: flex !important;
  align-items: center;
  gap: 8px;
  padding-bottom: 10px !important;
  color: #0E0F10 !important;
  font-size: 14px !important;
  line-height: 20px !important;
}

.lang-switcher-flag {
  width: 20px;
  height: 20px;
}

.sw-button-primary {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: fit-content;
  padding: 12px 16px !important;
  border-radius: 12px !important;
  background-color: #D81B5D !important;
  color: #FFFFFF !important;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  text-align: center;
  text-decoration: none;
  transition: color 150ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.sw-button-primary:hover {
  background-color: #AE134A !important;
  color: #FFFFFF !important;
}

.sw-sign-in-label {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.sw-account {
  position: relative;
}

.sw-account-label {
  min-width: 0;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Holds the label's place while the active workspace loads (see accountLabel); the margin makes up the 20px line, so the button keeps its height. */
.sw-account-label.is-loading {
  width: 96px;
  height: 12px;
  margin: 4px 0;
  border-radius: 6px;
  background-color: rgb(255 255 255 / 0.3);
}

.sw-account-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 40;
  padding-top: 8px;
}

.sw-dropdown-panel {
  min-width: 18rem;
  max-width: 22rem;
  padding: 12px;
  border: 1px solid #C1C1CC;
  border-radius: 12px;
  background-color: #FFFFFF;
  box-shadow: 9px 8px 32px 0 rgb(0 0 0 / 0.1);
}

.hamburger {
  position: relative;
  display: flex !important;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-right: -4px !important;
  color: #FFFFFF !important;
}

.hamburger-box {
  position: relative;
  display: block;
  width: 20px;
  height: 14px;
}

.hamburger-inner,
.hamburger-inner::before,
.hamburger-inner::after {
  position: absolute;
  left: 0;
  display: block;
  width: 20px;
  height: 2px;
  border-radius: 9999px;
  background-color: currentColor;
  content: "";
  transition: transform 300ms ease-out;
}

.hamburger-inner {
  top: 50%;
  transform: translateY(-50%);
}

.hamburger-inner::before {
  top: -6px;
}

.hamburger-inner::after {
  top: 6px;
}

.hamburger-inner.is-open {
  background-color: transparent;
}

.hamburger-inner.is-open::before {
  transform: translateY(6px) rotate(45deg);
}

.hamburger-inner.is-open::after {
  transform: translateY(-6px) rotate(-45deg);
}

.mobile-menu {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 300ms ease-out;
}

.mobile-menu.is-open {
  grid-template-rows: 1fr;
}

.mobile-menu-clip {
  min-height: 0;
  overflow: hidden;
  border-bottom-right-radius: 16px;
  border-bottom-left-radius: 16px;
}

/*
 * The whole open menu scrolls as one, below the 80px header bar, so the footer and the account panel under the
 * links stay reachable on short screens and in landscape (the page itself is locked while the menu is open).
 */
.mobile-menu-panel {
  position: relative;
  z-index: 1;
  max-height: calc(100vh - 80px);
  max-height: calc(100dvh - 80px);
  overflow-y: auto;
  overscroll-behavior: contain;
  border-top: 1px solid #E5E6ED;
  border-bottom-right-radius: 16px;
  border-bottom-left-radius: 16px;
  background-color: #FFFFFF;
}

.mobile-menu-scroll {
  padding: 8px 20px 0;
}

.mobile-menu-list {
  display: flex;
  flex-direction: column;
}

.mobile-menu-link {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 14px 0 !important;
  border-bottom: 1px solid #E5E6ED !important;
  color: #0E0F10 !important;
  font-size: 14px !important;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
}

.mobile-menu-list > li:last-child > .mobile-menu-link {
  border-bottom: 0 !important;
}

.mobile-menu-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-top: 1px solid #E5E6ED;
}

.mobile-menu-footer .lang-switcher-button {
  color: #0E0F10 !important;
}

.lang-switcher--up .lang-switcher-menu-wrapper {
  top: auto;
  right: auto;
  bottom: 100%;
  left: 0;
  padding-top: 0;
  padding-bottom: 8px;
}

/* Takes the room the language switcher leaves, so a long account label truncates instead of wrapping. */
.mobile-menu-account {
  display: flex;
  flex: 1 1 0;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  min-width: 0;
}

.mobile-menu-account .sw-sign-in {
  min-width: 0;
}

.mobile-menu-account-panel {
  width: 100%;
}

/* Header bar (80px) plus the workspace row (44px) below it. */
.sw-header-spacer {
  height: 80px;
  background-color: #000000;
}

.sw-header-spacer--subnav {
  height: 124px;
}

.sw-header-spacer--overlay {
  background-color: transparent;
}

.sw-subnav {
  display: grid;
  grid-template-rows: 1fr;
  color: #FFFFFF;
  transition: grid-template-rows 300ms ease-out;
}

.sw-subnav.is-collapsed {
  grid-template-rows: 0fr;
}

.sw-subnav-clip {
  position: relative;
  min-height: 0;
  overflow: hidden;
}

.sw-subnav-clip::before {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 1px;
  background-color: rgb(255 255 255 / 0.12);
  content: "";
}

/* Scrolls sideways when the links do not fit (phones, long translations). */
.sw-subnav-scroll {
  position: relative;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
}

.sw-subnav-scroll::-webkit-scrollbar {
  display: none;
}

.sw-subnav-scroll.fade-start {
  -webkit-mask-image: linear-gradient(to right, transparent, #000000 48px);
  mask-image: linear-gradient(to right, transparent, #000000 48px);
}

.sw-subnav-scroll.fade-end {
  -webkit-mask-image: linear-gradient(to left, transparent, #000000 48px);
  mask-image: linear-gradient(to left, transparent, #000000 48px);
}

.sw-subnav-scroll.fade-start.fade-end {
  -webkit-mask-image: linear-gradient(to right, transparent, #000000 48px, #000000 calc(100% - 48px), transparent);
  mask-image: linear-gradient(to right, transparent, #000000 48px, #000000 calc(100% - 48px), transparent);
}

/*
 * Centred in the header. The side padding equals the margin of .sw-container-wide, so a row that does not
 * fit (phones, long translations) grows to max-content and scrolls from the logo's edge instead.
 */
.sw-subnav .sw-subnav-list {
  display: flex;
  justify-content: center;
  gap: 24px;
  width: max-content;
  min-width: 100%;
  height: 44px;
  padding: 0 calc((100% - min(1440px, 90%)) / 2);
}

.sw-subnav-link {
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
}

.sw-subnav-link::after {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 2px;
  border-radius: 2px 2px 0 0;
  background-color: transparent;
  content: "";
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.sw-subnav-link:hover::after {
  background-color: rgb(255 255 255 / 0.4);
}

.sw-subnav-link.is-active::after {
  background-color: #D81B5D;
}

/* The row clips an outline, so keyboard focus shows on the indicator bar instead. */
.sw-subnav-link:focus-visible {
  outline: none;
}

.sw-subnav-link:focus-visible::after {
  background-color: #FFFFFF;
}

.sw-header .mobile-menu-list--account {
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #E5E6ED;
}

.mobile-menu-link.is-active {
  color: #D81B5D !important;
  font-weight: 600;
}

@media (min-width: 1280px) {
  .main-menu {
    display: flex !important;
  }

  .sw-header-actions {
    display: flex;
  }

  .hamburger,
  .mobile-menu {
    display: none !important;
  }

  .sw-subnav .sw-subnav-list {
    gap: 32px;
  }
}

.landing-navbar-overlay {
  position: fixed;
  inset: 0;
  z-index: 999998;
  background-color: rgb(0 0 0 / 0.45);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
}

@media (min-width: 1280px) {
  .landing-navbar-overlay {
    display: none;
  }
}

.mobile-menu-overlay-enter-active,
.mobile-menu-overlay-leave-active {
  transition: opacity 300ms ease-out;
}

.mobile-menu-overlay-enter-from,
.mobile-menu-overlay-leave-to {
  opacity: 0;
}
</style>
